<template>
  <div class="main bg-primary">
      <v-container class="py-6 container-wrapper">
        
      <div class="header-section">
        <TopDeck
                firstElement="Modification du"
                secondElement="profil"
                :coloredSecond="true"
                actionHeaderIcon="mdi-arrow-left"
                :actionheaderFunction="() => $router.back()"
                actionHeaderColor="accent"
                actionHeaderTextColor="white"
              />
      </div>

      <v-form ref="form" v-model="isValid" lazy-validation>
        <v-row>
          <v-col cols="12" v-for="q in visibleQuestions" :key="q.key">
            <v-card class="question-card bg-secondary" elevation="4">
              <v-card-title class="question-title">
                {{ t(q.key) }}
              </v-card-title>
              <v-card-text class="question-content">
                <!-- Type text -->
                <v-text-field
                  v-if="q.type === 'text'"
                  v-model="formData[q.key]"
                  type="text"
                  variant="outlined"
                  density="comfortable"
                  :maxlength="q.key === 'job' ? 100 : undefined"
                  :placeholder="getPlaceholder(q.key)"
                  :error-messages="getErrorMessage(q.key)"
                  @input="validateField(q.key)"
                  class="custom-input"
                />

                <!-- Type number avec conversion d'unités -->
                <div v-else-if="q.type === 'number'" class="number-input-wrapper">
                  <div class="unit-toggle">
                    <button
                      v-if="q.key === 'actualSize'"
                      type="button"
                      @click="heightUnit = 'cm'"
                      class="unit-btn"
                      :class="{ active: heightUnit === 'cm' }"
                    >
                      cm
                    </button>
                    <button
                      v-if="q.key === 'actualSize'"
                      type="button"
                      @click="heightUnit = 'ft'"
                      class="unit-btn"
                      :class="{ active: heightUnit === 'ft' }"
                    >
                      ft
                    </button>
                    <button
                      v-if="q.key === 'actualWeight' || q.key === 'targetWeight'"
                      type="button"
                      @click="weightUnit = 'kg'"
                      class="unit-btn"
                      :class="{ active: weightUnit === 'kg' }"
                    >
                      kg
                    </button>
                    <button
                      v-if="q.key === 'actualWeight' || q.key === 'targetWeight'"
                      type="button"
                      @click="weightUnit = 'lb'"
                      class="unit-btn"
                      :class="{ active: weightUnit === 'lb' }"
                    >
                      lb
                    </button>
                  </div>
                  <v-text-field
                    v-model="displayValues[q.key]"
                    type="number"
                    variant="outlined"
                    density="comfortable"
                    :min="0"
                    :step="getStep(q.key)"
                    :placeholder="getPlaceholder(q.key)"
                    :error-messages="getErrorMessage(q.key)"
                    @input="handleNumberInput(q.key)"
                    class="custom-input"
                  />
                </div>

                <!-- Type radio -->
                <v-radio-group
                  v-else-if="q.type === 'radio'"
                  v-model="formData[q.key]"
                  class="custom-radio-group"
                >
                  <div class="radio-options">
                    <div
                      v-for="opt in q.options"
                      :key="opt.option"
                      :class="[
                        'radio-option', 
                        { 
                          'selected': formData[q.key] === opt.option,
                          'option-disabled': isOptionDisabled(q.key, opt.option)
                        }
                      ]"
                      @click="!isOptionDisabled(q.key, opt.option) && (formData[q.key] = opt.option)"
                    >
                      <span class="option-text">{{ getOptionLabel(q.key, opt.option) }}</span>
                      <span v-if="isOptionDisabled(q.key, opt.option)" class="disabled-reason">
                        {{ getDisabledReason(q.key, opt.option) }}
                      </span>
                    </div>
                  </div>
                </v-radio-group>

                <!-- Type checkbox -->
                <div v-else-if="q.type === 'checkbox'" class="checkbox-container">
                  <div
                    v-for="opt in q.options"
                    :key="opt.option"
                    :class="['checkbox-option', { 'selected': formData[q.key]?.includes(opt.option) }]"
                    @click="toggleCheckbox(q.key, opt.option)"
                  >
                    <span class="option-text">{{ t(opt.option) }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Avertissement sportif de haut niveau -->
        <div v-if="showHighLevelAthleteWarning" class="warning-container">
          <div class="blocking-warning">
            <p class="warning-title">⚠️ Modification non autorisée</p>
            <p class="warning-text">
              Cette application n'est pas optimale pour les sportifs de haut niveau. 
              Nous vous recommandons de consulter un nutritionniste sportif spécialisé pour un suivi personnalisé adapté à vos besoins spécifiques.
            </p>
          </div>
        </div>

        <!-- Avertissement diabète + obésité -->
        <div v-if="showDiabetesWarning" class="warning-container">
          <div class="blocking-warning">
            <p class="warning-title">⚠️ Modification non autorisée</p>
            <p class="warning-text">
              Avec un IMC supérieur à 30 et du diabète, cette application n'est pas adaptée à votre situation. 
              Nous vous recommandons vivement de consulter un professionnel de santé pour un suivi personnalisé.
            </p>
          </div>
        </div>

        <!-- Avertissement régime flexitarien/pescetarien -->
        <div v-if="showDietWarning" class="warning-container">
          <div class="blocking-warning">
            <p class="warning-title">⚠️ Modification non autorisée</p>
            <p class="warning-text">
              Cette application n'est actuellement pas adaptée aux régimes flexitariens et pescetariens. 
              Nous travaillons pour intégrer ces options prochainement.
            </p>
          </div>
        </div>

        <!-- Avertissement plus de 7 séances par semaine -->
        <div v-if="showIntensiveWorkoutWarning" class="warning-container">
          <div class="info-warning">
            <p class="warning-title">⚠️ Attention</p>
            <p class="warning-text">
              Avec plus de 7 séances de sport par semaine, il se peut que l'application ne soit pas adaptée à 100% à vos besoins très spécifiques. 
              Un suivi nutritionnel personnalisé pourrait être plus approprié.
            </p>
          </div>
        </div>

        <!-- Avertissement repas incompatibles avec objectif poids -->
        <div v-if="showMealsWarning" class="warning-container">
          <div class="info-warning">
            <p class="warning-title">⚠️ Attention</p>
            <p class="warning-text">
              {{ mealsWarningMessage }}
            </p>
          </div>
        </div>

        <!-- Message d'erreur IMC -->
        <div v-if="showBMIError" class="warning-container">
          <div class="blocking-warning">
            <p class="warning-title">⚠️ Attention</p>
            <p class="warning-text">
              <span v-if="calculatedBMI < 15.5">
                Votre IMC actuel est de {{ calculatedBMI.toFixed(1) }}, ce qui est inférieur à 15.5.
              </span>
              <span v-else-if="targetBMI < 15.5">
                Votre poids cible donnerait un IMC de {{ targetBMI.toFixed(1) }}, ce qui est inférieur à 15.5.
              </span>
              Pour votre sécurité, nous vous recommandons de consulter un professionnel de santé.
            </p>
          </div>
        </div>

        <div class="action-buttons">
          <v-btn
            class="save-btn"
            color="accent"
            :disabled="!canSave"
            @click="saveProfile"
            size="large"
          >
            Sauvegarder
          </v-btn>
          <v-btn
            class="cancel-btn"
            variant="outlined"
            @click="cancel"
            size="large"
          >
            Annuler
          </v-btn>
        </div>
      </v-form>
    </v-container>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      right
      :timeout="3500"
    >
      {{ errorMessage || snackbarMessage }}
    </v-snackbar>
  </div>
</template>


<script setup>
import { reactive, watch, ref, computed } from "vue"
import { useRouter } from 'vue-router';
import { useAuthStore } from "@/store/auth_store"
import questionsData from "@/data/register_questions.json"
import { updateUserInfos } from "@/service/user_service"
import { useI18n } from 'vue-i18n'
import TopDeck from '@/components/Utils/TopDeck.vue'

const { t } = useI18n();

const auth = useAuthStore()
const router = useRouter();
const questions = questionsData

const snackbar = ref(false);
const errorMessage = ref("");
const snackbarMessage = ref("");
const snackbarColor = ref("success");
const fieldErrors = ref({});
const calculatedBMI = computed(() => currentBMI.value);

const targetBMI = computed(() => {
  const targetWeight = parseFloat(formData.targetWeight);
  const heightCm = parseFloat(formData.actualSize);
  if (targetWeight && heightCm && heightCm > 0) {
    const heightM = heightCm / 100;
    return targetWeight / (heightM * heightM);
  }
  return 0;
});

const showBMIError = computed(() => {
  if (currentBMI.value > 0 && currentBMI.value < 15.5) return true;
  if (targetBMI.value > 0 && targetBMI.value < 15.5) return true;
  return false;
});
const heightUnit = ref('cm');
const weightUnit = ref('kg');
const displayValues = ref({});

const showSnack = (msg, color = "success") => {
  snackbarMessage.value = msg;
  snackbarColor.value = color;
  snackbar.value = true;
};

const formData = reactive(
  questions.reduce((acc, q) => {
    acc[q.key] = q.type === "checkbox" ? [] : ""
    return acc
  }, {})
)
const isValid = ref(true)

const currentBMI = computed(() => {
  const weight = parseFloat(formData.actualWeight);
  const heightCm = parseFloat(formData.actualSize);
  
  if (weight && heightCm && heightCm > 0) {
    const heightM = heightCm / 100;
    return weight / (heightM * heightM);
  }
  return 0;
});

const showHighLevelAthleteWarning = computed(() => {
  return formData.isHighLevelAthlete === 'yes';
});

const showDiabetesWarning = computed(() => {
  const hasDiabetes = Array.isArray(formData.medicalConditions) && 
                      formData.medicalConditions.includes('diabetes');
  return hasDiabetes && currentBMI.value > 30;
});

const showDietWarning = computed(() => {
  return Array.isArray(formData.diet) && 
         (formData.diet.includes('flexitarian') || formData.diet.includes('pescatarian'));
});

const showIntensiveWorkoutWarning = computed(() => {
  return formData.weeklyWorkouts === 'moreThan7';
});

const showMealsWarning = computed(() => {
  const weightObjective = formData.weightObjective;
  const currentMeals = formData.takenMeals || [];
  const futureMeals = formData.takenMealsObjectives || [];
  
  if (!weightObjective || currentMeals.length === 0 || futureMeals.length === 0) return false;
  
  if (weightObjective === 'gainWeight' && futureMeals.length < currentMeals.length) {
    return true;
  }
  
  if (weightObjective === 'loseWeight' && futureMeals.length > currentMeals.length) {
    return true;
  }
  
  return false;
});

const mealsWarningMessage = computed(() => {
  const weightObjective = formData.weightObjective;
  
  if (weightObjective === 'gainWeight') {
    return "Vous souhaitez prendre du poids mais prévoyez de faire moins de repas. Cela pourrait rendre votre objectif plus difficile à atteindre.";
  }
  
  if (weightObjective === 'loseWeight') {
    return "Vous souhaitez perdre du poids mais prévoyez de faire plus de repas. Cela pourrait rendre votre objectif plus difficile à atteindre.";
  }
  
  return "";
});

const canSave = computed(() => {
  return isValid.value && 
         !showBMIError.value && 
         !showHighLevelAthleteWarning.value && 
         !showDiabetesWarning.value &&
         !showDietWarning.value;
});

function getStep(key) {
  if (key === 'actualSize') {
    return heightUnit.value === 'cm' ? '0.1' : '0.01';
  }
  if (key === 'actualWeight' || key === 'targetWeight') {
    return weightUnit.value === 'kg' ? '0.1' : '0.1';
  }
  return '0.1';
}

function getPlaceholder(key) {
  const placeholders = {
    actualWeight: weightUnit.value === 'kg' ? "Poids en kg" : "Poids en lb",
    actualSize: heightUnit.value === 'cm' ? "Taille en cm" : "Taille en pieds (ex: 5.9)",
    targetWeight: weightUnit.value === 'kg' ? "Poids cible en kg" : "Poids cible en lb",
    job: "Entrez votre métier (100 caractères max)",
    dailySteps: "Nombre de pas par jour"
  };
  return placeholders[key] || "Entrez votre réponse";
}

function updateDisplayValue(key) {
  if (!formData[key] || formData[key] === '') {
    displayValues.value[key] = '';
    return;
  }
  
  const numValue = parseFloat(formData[key]);
  
  if (key === 'actualSize') {
    if (heightUnit.value === 'ft') {
      const totalInches = numValue / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = (totalInches % 12).toFixed(1);
      displayValues.value[key] = `${feet}.${inches.replace('.', '')}`;
    } else {
      displayValues.value[key] = numValue.toString();
    }
  } else if (key === 'actualWeight' || key === 'targetWeight') {
    if (weightUnit.value === 'lb') {
      displayValues.value[key] = (numValue * 2.20462).toFixed(1);
    } else {
      displayValues.value[key] = numValue.toString();
    }
  } else {
    displayValues.value[key] = formData[key];
  }
}

function handleNumberInput(key) {
  const displayVal = displayValues.value[key];
  
  if (!displayVal || displayVal === '') {
    formData[key] = '';
    validateField(key);
    return;
  }
  
  const numValue = parseFloat(displayVal);
  
  if (key === 'actualSize') {
    if (heightUnit.value === 'ft') {
      const feet = Math.floor(numValue);
      const inches = (numValue - feet) * 100;
      const totalInches = (feet * 12) + inches;
      formData[key] = (totalInches * 2.54).toFixed(1);
    } else {
      formData[key] = numValue.toString();
    }
  } else if (key === 'actualWeight' || key === 'targetWeight') {
    if (weightUnit.value === 'lb') {
      formData[key] = (numValue / 2.20462).toFixed(1);
    } else {
      formData[key] = numValue.toString();
    }
  } else {
    formData[key] = displayVal;
  }
  
  validateField(key);
}

watch(heightUnit, () => {
  updateDisplayValue('actualSize');
});

watch(weightUnit, () => {
  updateDisplayValue('actualWeight');
  updateDisplayValue('targetWeight');
});

function getOptionLabel(key, option) {
  if (key === 'weeklyWorkouts' && !isNaN(option)) {
    return option;
  }
  if (option === 'moreThan7') {
    return '+ de 7';
  }
  return t(option);
}

function isOptionDisabled(key, option) {
  if (key !== 'weightObjective') return false;
  
  const bmi = currentBMI.value;
  
  if (bmi > 0 && bmi < 15.5) {
    return option === 'maintainWeight' || option === 'loseWeight';
  }
  
  return false;
}

function getDisabledReason(key, option) {
  if (key === 'weightObjective' && currentBMI.value < 15.5) {
    if (option === 'maintainWeight' || option === 'loseWeight') {
      return '(IMC trop bas)';
    }
  }
  return '';
}

function getErrorMessage(key) {
  return fieldErrors.value[key] || "";
}

function validateField(key) {
  if (key === 'job') {
    if (!formData[key] || formData[key].trim().length === 0) {
      fieldErrors.value[key] = "Veuillez entrer votre métier";
      return false;
    }
    if (formData[key].length > 100) {
      fieldErrors.value[key] = "Le métier ne peut pas dépasser 100 caractères";
      return false;
    }
    fieldErrors.value[key] = "";
    return true;
  }

  const question = questions.find(q => q.key === key);
  if (question && question.type === 'text') {
    fieldErrors.value[key] = "";
    return true;
  }

  const value = parseFloat(formData[key]);

  if (isNaN(value) || value < 0) {
    fieldErrors.value[key] = "Veuillez entrer un nombre valide et positif";
    return false;
  }

  if (key === 'dailySteps') {
    if (value < 0 || value > 100000) {
      fieldErrors.value[key] = "Veuillez entrer un nombre de pas réaliste";
      return false;
    }
    if (value < 7000) {
      console.log('Attention, pas inférieurs à 7000');
    }
    fieldErrors.value[key] = "";
    return true;
  }

  if (key === 'actualWeight' && (value < 20 || value > 300)) {
    fieldErrors.value[key] = "Le poids doit être entre 20 et 300 kg";
    return false;
  }

  if (key === 'actualSize' && (value < 50 || value > 250)) {
    fieldErrors.value[key] = "La taille doit être entre 50 et 250 cm";
    return false;
  }

  if (key === 'targetWeight') {
    const actualWeight = parseFloat(formData.actualWeight);
    const weightObjective = formData.weightObjective;
    const heightCm = parseFloat(formData.actualSize);

    if (weightObjective === 'loseWeight' && value >= actualWeight) {
      fieldErrors.value[key] = "Le poids cible doit être inférieur au poids actuel";
      return false;
    }

    if (weightObjective === 'gainWeight' && value <= actualWeight) {
      fieldErrors.value[key] = "Le poids cible doit être supérieur au poids actuel";
      return false;
    }

    if (heightCm && heightCm > 0) {
      const heightM = heightCm / 100;
      const targetBMICalc = value / (heightM * heightM);
      
      if (targetBMICalc < 15.5) {
        fieldErrors.value[key] = `Ce poids donnerait un IMC de ${targetBMICalc.toFixed(1)} (trop bas, minimum 15.5)`;
        return false;
      }
    }

    if (value < 20 || value > 300) {
      fieldErrors.value[key] = "Le poids cible doit être entre 20 et 300 kg";
      return false;
    }
  }

  fieldErrors.value[key] = "";
  return true;
}

function checkBMI() {
  const weight = parseFloat(formData.actualWeight);
  const heightCm = parseFloat(formData.actualSize);
  
  if (weight && heightCm && heightCm > 0) {
    const heightM = heightCm / 100;
    const bmi = weight / (heightM * heightM);
    
    if (bmi < 15.5) {
      return false;
    }
  }

  const targetWeight = parseFloat(formData.targetWeight);
  if (targetWeight && heightCm && heightCm > 0 && formData.weightObjective !== 'maintainWeight') {
    const heightM = heightCm / 100;
    const targetBMICalc = targetWeight / (heightM * heightM);
    
    if (targetBMICalc < 15.5) {
      return false;
    }
  }
  return true;
}

function validateAllFields() {
  let allValid = true;

  if (formData.job) {
    if (!validateField('job')) {
      allValid = false;
    }
  }

  ['actualWeight', 'actualSize', 'targetWeight', 'dailySteps'].forEach(key => {
    if (formData[key] && formData[key] !== '') {
      if (!validateField(key)) {
        allValid = false;
      }
    }
  });

  if (!checkBMI()) {
    allValid = false;
  }

  isValid.value = allValid;
}

function shouldShowQuestion(question) {
  if (!question.conditional || !question.showIf) {
    return true;
  }

  const { key, values } = question.showIf;
  const responseValue = formData[key];

  if (Array.isArray(values)) {
    return values.includes(responseValue);
  }

  return responseValue === values;
}

function toggleCheckbox(key, option) {
  if (!Array.isArray(formData[key])) {
    formData[key] = [];
  }
  
  const index = formData[key].indexOf(option);
  if (index > -1) {
    formData[key].splice(index, 1);
  } else {
    formData[key].push(option);
  }
}

const visibleQuestions = computed(() => {
  return questions.filter(q => shouldShowQuestion(q));
});

watch(
  () => auth.userInfos,
  (u) => {
    if (!u) return
    questions.forEach((q) => {
      if (u[q.key] !== undefined) {
        formData[q.key] = Array.isArray(u[q.key]) ? [...u[q.key]] : u[q.key]
      } else if (q.type === "checkbox") {
        formData[q.key] = []
      } else {
        formData[q.key] = ""
      }
      updateDisplayValue(q.key);
    })
    validateAllFields();
  },
  { immediate: true, deep: true }
)

function calculateNAP(dailySteps, activityLevel, weeklyWorkouts) {
  let nap = 0;

  const highSteps = dailySteps === 'from7000to10000' || dailySteps === 'moreThan10000';

  if (!highSteps) {
    switch (activityLevel) {
      case 'sedentary':        nap = 1.4; break;
      case 'lightlyActive':    nap = 1.6; break;
      case 'moderatelyActive': nap = 1.8; break;
      case 'veryActive':       nap = 2.2; break;
      default: nap = 1.4;
    }
  } else {
    switch (activityLevel) {
      case 'sedentary':        nap = 1.5; break;
      case 'lightlyActive':    nap = 1.7; break;
      case 'moderatelyActive': nap = 1.8; break;
      case 'veryActive':       nap = 2.2; break;
      default: nap = 1.5;
    }
  }

  const workouts = weeklyWorkouts === 'moreThan7' ? 8 : parseInt(weeklyWorkouts) || 0;

  if (workouts >= 2 && workouts <= 3)     nap += 0.05;
  else if (workouts > 3 && workouts <= 4) nap += 0.10;
  else if (workouts > 4 && workouts <= 6) nap += 0.15;
  else if (workouts > 6)                  nap += 0.20;

  return Math.round(nap * 100) / 100;
}

watch(
  () => [formData.dailySteps, formData.activityLevel, formData.weeklyWorkouts],
  ([steps, activity, workouts]) => {
    if (steps && activity && workouts) {
      formData.NAP = calculateNAP(steps, activity, workouts);
    }
  },
  { deep: true }
);

async function saveProfile() {
  if (!canSave.value) {
    showSnack("Veuillez corriger les erreurs avant de sauvegarder", "error");
    return;
  }

  if (formData.dailySteps && formData.activityLevel && formData.weeklyWorkouts) {
    formData.NAP = calculateNAP(formData.dailySteps, formData.activityLevel, formData.weeklyWorkouts);
  }

  const dataToSave = { ...formData };
  delete dataToSave.originalWeight;

  auth.userInfos = { ...auth.userInfos, ...dataToSave }
  if (auth.userid) {
    try {
      await updateUserInfos(auth.userid, dataToSave);
      showSnack("Profil mis à jour avec succès", "success");
      await new Promise(resolve => setTimeout(resolve, 500));
      router.push('/profile');
    } catch (e) {
      console.error('Erreur lors de la mise à jour du profil utilisateur', e)
      showSnack("Erreur lors de la mise à jour du profil utilisateur", "error");
    }
  }
}

function resetForm() {
  if (!auth.userInfos) return
  questions.forEach((q) => {
    formData[q.key] = Array.isArray(auth.userInfos[q.key])
      ? [...auth.userInfos[q.key]]
      : auth.userInfos[q.key] ?? (q.type === "checkbox" ? [] : "")
    updateDisplayValue(q.key);
  })
  fieldErrors.value = {};
}

function cancel() {
  resetForm();
  router.push('/profile');
}
</script>

<style scoped>
.main {
  min-height: 100vh;
  padding: 2em 0;
}

.container-wrapper {
  max-width: 900px;
}

.header-section {
  margin-bottom: 2em;
  text-align: center;
}

.page-title {
  font-size: 2em;
  font-weight: 600;
  color: white;
}

.question-card {
  border-radius: 1em;
  margin-bottom: 1.5em;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.question-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.question-title {
  font-size: 1.1em;
  font-weight: 600;
  padding: 1.2em 1.5em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.question-content {
  padding: 1.5em;
}

.custom-input {
  margin-top: 0.5em;
}

.number-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.unit-toggle-container {
  display: flex;
  justify-content: center;
}

.unit-toggle {
  display: flex;
  gap: 0.5em;
  justify-content: center;
}

.unit-btn {
  padding: 0.5em 1.5em;
  border: 2px solid transparent;
  background-color: rgb(var(--v-theme-tertiary));
  border-radius: 0.5em;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
}

.unit-btn.active {
  border: 3px solid rgb(var(--v-theme-accent));
  background-color: rgba(var(--v-theme-accent), 0.1);
}

.radio-options,
.checkbox-container {
  display: flex;
  flex-direction: column;
  gap: 0.8em;
}

.radio-option,
.checkbox-option {
  padding: 1em 1.5em;
  border-radius: 0.5em;
  background-color: rgb(var(--v-theme-tertiary));
  border: 3px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.radio-option:hover,
.checkbox-option:hover {
  border: 3px solid rgb(var(--v-theme-accent));
}

.radio-option:hover.option-disabled,
.checkbox-option:hover.option-disabled {
  border: 3px solid transparent;
  cursor: not-allowed;
}

.radio-option.selected,
.checkbox-option.selected {
  border: 3px solid rgb(var(--v-theme-accent));
  background-color: rgba(var(--v-theme-accent), 0.1);
}

.option-disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  background-color: #6b7280 !important;
}

.disabled-reason {
  font-size: 0.8em;
  color: #ef4444;
  margin-top: 0.25em;
  font-style: italic;
}

.option-text {
  font-weight: 500;
  display: block;
  text-align: center;
}

.warning-container {
  margin: 2em 0;
}

.blocking-warning {
  padding: 1.5em;
  background-color: #fee;
  border: 2px solid #ef4444;
  border-radius: 0.75em;
  animation: shake 0.5s;
}

.info-warning {
  padding: 1.5em;
  background-color: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 0.75em;
}

.warning-title {
  color: #b91c1c;
  font-weight: 600;
  font-size: 1.1em;
  margin-bottom: 0.5em;
}

.info-warning .warning-title {
  color: #d97706;
}

.warning-text {
  color: #dc2626;
  line-height: 1.5;
}

.info-warning .warning-text {
  color: #d97706;
}

.action-buttons {
  display: flex;
  gap: 1em;
  margin-top: 2em;
  justify-content: center;
  flex-wrap: wrap;
}

.save-btn,
.cancel-btn {
  min-width: 150px;
  font-weight: 600;
  border-radius: 999px;
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 1.5em;
  }

  .action-buttons {
    flex-direction: column;
  }

  .save-btn,
  .cancel-btn {
    width: 100%;
  }
}
</style>
